/**
 * Pictogram : rend un pictogramme SVG au trait à partir d'une clé sémantique.
 *
 * Remplace les émojis utilisés comme icônes. Les données (ex. `icon: 'tree'`)
 * portent une clé, jamais un caractère émoji ; ce composant fait la
 * correspondance avec le système d'icônes (src/components/icons.tsx).
 * Une clé inconnue ne rend rien (pas de caractère de repli).
 */
import type { ComponentType } from 'react'
import {
  AlertTriangleIcon,
  AppleIcon,
  BanIcon,
  BarChartIcon,
  BCorpIcon,
  BedIcon,
  BookIcon,
  BuildingIcon,
  ButterflyIcon,
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  ClockIcon,
  CloudIcon,
  CoinsIcon,
  CookieIcon,
  CowIcon,
  DeciduousTreeIcon,
  DropletIcon,
  HandshakeLineIcon,
  LeafOutlineIcon,
  MedalIcon,
  RocketLineIcon,
  ScaleIcon,
  EggIcon,
  FactoryIcon,
  FileTextIcon,
  FlagIcon,
  FlameIcon,
  FlaskIcon,
  FlowerIcon,
  FolderIcon,
  FuelIcon,
  GlobeIcon,
  HammerIcon,
  HardHatIcon,
  HeartPulseIcon,
  HelpCircleIcon,
  HomeIcon,
  LabelIcon,
  LandmarkIcon,
  LandscapeIcon,
  LightbulbIcon,
  LinkIcon,
  LockClosedIcon,
  MailIcon,
  MapPinIcon,
  MessageIcon,
  MonitorIcon,
  NewspaperIcon,
  PackageIcon,
  PenIcon,
  PineIcon,
  PlaneIcon,
  PlugIcon,
  RadioIcon,
  RefreshIcon,
  RulerIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  SproutIcon,
  StarIcon,
  StoreIcon,
  TargetIcon,
  TractorIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  TruckIcon,
  UsersIcon,
  VoteIcon,
  WheatIcon,
  WrenchIcon,
  XCircleIcon,
  ZapIcon,
} from '@/components/icons'

type IconComponent = ComponentType<{ className?: string }>

export const PICTOGRAMS: Record<string, IconComponent> = {
  // Nature, forêt, agriculture
  tree: DeciduousTreeIcon,
  pine: PineIcon,
  leaf: LeafOutlineIcon,
  sprout: SproutIcon,
  wheat: WheatIcon,
  apple: AppleIcon,
  cow: CowIcon,
  egg: EggIcon,
  flower: FlowerIcon,
  butterfly: ButterflyIcon,
  droplets: DropletIcon,
  landscape: LandscapeIcon,
  tractor: TractorIcon,
  // Acteurs, lieux
  building: BuildingIcon,
  factory: FactoryIcon,
  landmark: LandmarkIcon,
  store: StoreIcon,
  home: HomeIcon,
  hotel: BedIcon,
  construction: HardHatIcon,
  truck: TruckIcon,
  plane: PlaneIcon,
  fuel: FuelIcon,
  users: UsersIcon,
  handshake: HandshakeLineIcon,
  globe: GlobeIcon,
  flag: FlagIcon,
  'map-pin': MapPinIcon,
  // Documents, données
  chart: BarChartIcon,
  'trending-up': TrendingUpIcon,
  'trending-down': TrendingDownIcon,
  clipboard: ClipboardListIcon,
  document: FileTextIcon,
  book: BookIcon,
  newspaper: NewspaperIcon,
  pen: PenIcon,
  folder: FolderIcon,
  package: PackageIcon,
  mail: MailIcon,
  calendar: CalendarIcon,
  camera: CameraIcon,
  message: MessageIcon,
  vote: VoteIcon,
  // Garanties, méthode
  shield: ShieldIcon,
  lock: LockClosedIcon,
  award: MedalIcon,
  label: LabelIcon,
  bcorp: BCorpIcon,
  star: StarIcon,
  balance: ScaleIcon,
  search: SearchIcon,
  target: TargetIcon,
  link: LinkIcon,
  refresh: RefreshIcon,
  check: CheckCircleIcon,
  cross: XCircleIcon,
  ban: BanIcon,
  alert: AlertTriangleIcon,
  help: HelpCircleIcon,
  idea: LightbulbIcon,
  // Outils, technique
  settings: SettingsIcon,
  wrench: WrenchIcon,
  hammer: HammerIcon,
  flask: FlaskIcon,
  monitor: MonitorIcon,
  plug: PlugIcon,
  radio: RadioIcon,
  zap: ZapIcon,
  flame: FlameIcon,
  cloud: CloudIcon,
  health: HeartPulseIcon,
  coins: CoinsIcon,
  clock: ClockIcon,
  ruler: RulerIcon,
  rocket: RocketLineIcon,
  cookie: CookieIcon,
}

interface PictogramProps {
  name: string | undefined | null
  className?: string
}

export function Pictogram({ name, className = 'w-6 h-6' }: PictogramProps) {
  const Icon = name ? PICTOGRAMS[name] : undefined
  if (!Icon) return null
  // Les grands pictogrammes gardent un trait fin : le trait de base (2) est calé pour 24 px.
  const fin = /(^|\s)(md:|lg:)?w-(10|12|16)(\s|$)/.test(className)
    ? ' [stroke-width:1.5]'
    : /(^|\s)(md:|lg:)?w-(7|8|9)(\s|$)/.test(className)
      ? ' [stroke-width:1.75]'
      : ''
  return <Icon className={className + fin} />
}

export default Pictogram
